
  import ModalDialog from "@/components/ModalDialog.vue";
  import Employment from "@/model/Employment";
  import EmployeeEmploymentForm from "../forms/EmployeeEmploymentForm.vue";
  import {computed, defineComponent, reactive, Ref, SetupContext, toRefs, watch} from "vue";
  import {helpers, required} from "@vuelidate/validators";
  import useVuelidate from "@vuelidate/core";
  import moment from "moment";
  import EmployeeService from "@/service/EmployeeService";
  import {AxiosError} from "axios";
  import store from "@/store/store";
  import {isAuthorized} from "@/mixin/mixins";
  import eventBus from "@/eventbus";
  import {MessageTypes} from "@/components/message-types";

  export default defineComponent({
    name: "EmploymentDialog",
    components: {
      EmployeeEmploymentForm,
      ModalDialog
    },
    props: {
      editable: {
        type: Boolean,
        default: false
      },
      employeeUuid: {
        type: String,
        default: null
      },
      employment: {
        type: Employment,
        default: () => new Employment(),
      },
      value: {
        type: Boolean,
        default: false
      }
    },
    setup(props, context: SetupContext) {
      const initialState = {
        editItem: new Employment() as Employment,
        serverErrors: new Map<string, string>()
      };

      const state = reactive({...initialState});

      const rules = computed(() => ({
        editItem: {
          validFrom: {
            required: helpers.withMessage("Gültig ab ist erforderlich.", required),
          },
          employmentType: {
            required: helpers.withMessage("Beschäftigungsart ist erforderlich.", required),
          }
        }
      }));

      // @ts-ignore
      const v$ = useVuelidate(rules, state);

      const errors = computed(() => {
        const errors = new Map<string, string | Ref<string>>();
        if (state.serverErrors.size > 0) {
          return state.serverErrors;
        } else {
          const model = v$.value.editItem;
          if (model.$dirty) {
            if(model.validFrom.$error) {
              errors.set("validFrom", model.validFrom.$errors[0].$message);
            }
            if(model.employmentType.$error) {
              errors.set("employmentType", model.employmentType.$errors[0].$message);
            }
          }
        }

        return errors;
      });

      const deletable = computed(() => {
        // @ts-ignore
        return props.editable && isAuthorized("Superadmin") && !!props.employment && !!props.employment.uuid;
      });

      const earliestValidFrom = computed(() => {
        return store.state.general.earliestValidFrom;
      });

      const isEditable = computed(() => {
        return props.editable && props.employment && moment(props.employment.validFrom).isSameOrAfter(moment(earliestValidFrom.value));
      });

      const smartphone = computed(() => {
        return store.state.general.earliestValidFrom;
      });

      const close = () => {
        context.emit("input", false);
        context.emit("close");
      };

      // The v3 API answers a rejected employment with a problem detail instead of errors per field. A 409 is already
      // shown by the global error handler, a 422 is not.
      const showProblem = (error: AxiosError) => {
        if (error && error.response && error.response.status === 422) {
          // @ts-ignore
          eventBus.$emit(MessageTypes.ERROR, error.response.data.detail || error.response.data.title);
        }
      };

      const deleteEmployment = () => {
        EmployeeService.deleteEmploymentV3(props.employeeUuid, props.employment.uuid as string)
          .then(response => {
            context.emit("delete", props.employment);
            close();
          })
          .catch(showProblem);
      };

      const save = () => {
        state.serverErrors = new Map<string, string>();
        v$.value.$reset();
        v$.value.$touch();

        if (!v$.value.$invalid) {
          const employment = state.editItem;
          let promise = null;
          let eventType = "";
          if (props.employment.uuid) {
            promise = EmployeeService.updateEmploymentV3(props.employeeUuid, props.employment.uuid, {
              employmentType: employment.employmentType
            });
            eventType = "update";
          } else {
            promise = EmployeeService.createEmploymentV3(props.employeeUuid, {
              employmentType: employment.employmentType,
              // The date picker only selects a month, while the v3 API expects a full date
              validFrom: moment(employment.validFrom).startOf("month").format("YYYY-MM-DD")
            });
            eventType = "create";
          }

          promise
            .then(employmentPeriod => {
              context.emit(eventType, employmentPeriod);
              close();
            })
            .catch(showProblem);
        }
      };

      watch(() => props.employment, (newValue, oldValue) => {
        v$.value.$reset();
        state.editItem = Object.assign(new Employment(), newValue);
      });

      return {...toRefs(state),
              v$,
              errors,
              deletable,
              isEditable,
              smartphone,
              close,
              deleteEmployment,
              save
      };
    }
  });
